import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, ProgressBar, useTheme } from 'react-native-paper';
import type { ConfidenceLevel } from './types';

interface ConfidenceIndicatorProps {
  confidence: number;
  showLabel?: boolean;
  showDescription?: boolean;
  size?: 'small' | 'medium' | 'large';
  style?: any;
}

const getConfidenceLevel = (confidence: number): ConfidenceLevel => {
  if (confidence >= 0.8) {
    return {
      value: confidence,
      label: 'Very Confident',
      color: '#4CAF50',
      description: 'High confidence in this assessment',
    };
  } else if (confidence >= 0.6) {
    return {
      value: confidence,
      label: 'Confident',
      color: '#8BC34A',
      description: 'Good confidence level',
    };
  } else if (confidence >= 0.4) {
    return {
      value: confidence,
      label: 'Somewhat Confident',
      color: '#FFC107',
      description: 'Moderate confidence',
    };
  } else if (confidence >= 0.2) {
    return {
      value: confidence,
      label: 'Low Confidence',
      color: '#FF9800',
      description: 'Lower confidence in this assessment',
    };
  } else {
    return {
      value: confidence,
      label: 'Very Low Confidence',
      color: '#F44336',
      description: 'Very low confidence - may need more information',
    };
  }
};

export function ConfidenceIndicator({
  confidence,
  showLabel = true,
  showDescription = false,
  size = 'medium',
  style,
}: ConfidenceIndicatorProps) {
  const theme = useTheme();
  const confidenceLevel = getConfidenceLevel(confidence);

  const sizeStyles = {
    small: { height: 4, fontSize: 12 },
    medium: { height: 6, fontSize: 14 },
    large: { height: 8, fontSize: 16 },
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.progressContainer}>
        <ProgressBar
          progress={confidence}
          color={confidenceLevel.color}
          style={[styles.progressBar, { height: sizeStyles[size].height }]}
        />
      </View>

      {showLabel && (
        <View style={styles.labelContainer}>
          <Text
            variant='bodySmall'
            style={[
              styles.label,
              {
                color: confidenceLevel.color,
                fontSize: sizeStyles[size].fontSize,
              },
            ]}
          >
            {confidenceLevel.label}
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.percentage,
              { fontSize: sizeStyles[size].fontSize - 2 },
            ]}
          >
            {Math.round(confidence * 100)}%
          </Text>
        </View>
      )}

      {showDescription && (
        <Text
          variant='bodySmall'
          style={[styles.description, { color: theme.colors.onSurfaceVariant }]}
        >
          {confidenceLevel.description}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 4,
  },
  progressContainer: {
    marginBottom: 4,
  },
  progressBar: {
    borderRadius: 4,
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
  labelContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontWeight: '600',
  },
  percentage: {
    opacity: 0.7,
    marginLeft: 8,
  },
  description: {
    marginTop: 2,
    fontStyle: 'italic',
  },
});
